import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFocusEffect } from "@react-navigation/native";
import { debounce } from "lodash";

/**
 * A searchable, refreshable list screen's data: loads on focus (and again
 * when `deps` change while focused), reloads on a debounced search and on
 * pull-to-refresh. Errors are logged and keep the last data.
 *
 * @param fetcher      ({ search }) => Promise<data>
 * @param initialData  data before the first load (keep it a stable constant)
 * @param deps         values that change what `fetcher` fetches (e.g. tab)
 */
export function useSearchableList(
  fetcher,
  { initialData, deps = [], debounceMs = 500 } = {},
) {
  const [data, setData] = useState(initialData);
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Refs so a reload always uses the current fetcher and search text, not
  // the ones captured when a callback was created.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const searchRef = useRef("");

  const reload = useCallback(async (search = searchRef.current) => {
    try {
      setIsLoading(true);
      setData(await fetcherRef.current({ search }));
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      reload();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps),
  );

  const debouncedReload = useMemo(
    () => debounce((text) => reload(text), debounceMs),
    [reload, debounceMs],
  );
  useEffect(() => () => debouncedReload.cancel(), [debouncedReload]);

  const onChangeSearchText = useCallback(
    (text) => {
      searchRef.current = text;
      setSearchText(text);
      debouncedReload(text);
    },
    [debouncedReload],
  );

  const onRefresh = useCallback(() => {
    setIsRefreshing(true);
    reload().finally(() => setIsRefreshing(false));
  }, [reload]);

  return {
    data,
    searchText,
    onChangeSearchText,
    isLoading,
    isRefreshing,
    onRefresh,
    reload,
  };
}
